import {
  Box,
  Breadcrumbs,
  Typography,
  Container,
  Stack,
  Link,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Checkbox,
  Button,
  Dialog,
  SelectChangeEvent,
  FormControlLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CupsData,
  GetBillingTypeRequestBody,
  SetBillingTypeRequestBody,
  SuppliesBills,
  SuppliesBillsHeaderData,
  TabsDataBillsHeader,
} from "../../../core/models/bills/bills.model";
import "./BillsHeader.scss";
import { useNavigate } from "react-router-dom";
import { SuppliesDetailObject } from "../../../core/models/suppliesDetail/suppliesDetail.model";
import EnvirormentContent from "../../EnvirormentContent/EnvirormentContent";
import theme from "../../../muiTheme";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { formatNumber } from "../../../utils/formatters/formatters.util";
import { useContext, useEffect, useState } from "react";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import React from "react";
import billingTypesJSON from "../../../assets/data/billingTypes.json";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import {
  getBillingType,
  setBillingType,
} from "../../../core/services/bills/bills.service";
import SnackbarComponent from "../../UI/SnackbarComponent/SnackbarComponent";

interface BillsHeaderProps {
  totalSuppliesBills: SuppliesBills;
  isDesktopViewport: boolean;
  suppliesDetail: SuppliesDetailObject;
  isHeaderLoading: boolean;
  idCups: string | undefined;
  selectedTab: string;
  setSelectedTab: React.Dispatch<React.SetStateAction<string>>;
}

const BillsHeader = ({
  totalSuppliesBills,
  idCups,
  isDesktopViewport,
  suppliesDetail,
  selectedTab,
  setSelectedTab,
  isHeaderLoading,
}: BillsHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [initialValue, setInitialValue] = useState<string>("");
  const [selectedValue, setSelectedValue] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { ownerData, userData, snackbarData, setSnackbarData } =
    useContext(TotalEnergiesContext);
  const [supplyContractId, setSupplyContractId] = useState<string>("");

  const suppliesBillsHeaderData: SuppliesBillsHeaderData[] = [
    {
      id: "supplies",
      label: "Suministros",
      classNameIcon: "t-icon-map",
    },
    {
      id: "bills",
      label: "Facturas",
      classNameIcon: "t-icon-file",
    },
  ];

  const cupsData: CupsData[] = [
    {
      id: "service",
      label: "Servicio",
      value: suppliesDetail.service,
      renderContent: (service: string) => (
        <EnvirormentContent service={service} variant={"subtitle1"} />
      ),
    },
    { id: "cups", label: "Tablas.CUPS", value: suppliesDetail.cupsCode },
    {
      id: "suppliesDirection",
      label: "Facturas Página.Dirección del suministro",
      value:
        suppliesDetail.address === "-"
          ? "-"
          : `${suppliesDetail.address}, ${suppliesDetail.addressNumber}`,
    },
    {
      id: "location",
      label: "Facturas Página.Localidad",
      value: suppliesDetail.town,
    },
  ];

  const tabsDataBillsHeader: TabsDataBillsHeader[] = [
    {
      id: "invoice-history",
      value: "invoiceHistory",
      label: "Histórico de facturas",
    },
    {
      id: "evolution",
      value: "evolution",
      label: "Evolución de importes",
    },
  ];

  const billingArray = Object.values(billingTypesJSON);
  const menuItemsBilling = [
    ...new Set(billingArray.map(({ id, label }) => ({ id, label }))),
  ].sort();

  useEffect(() => {
    const fetchInitialBillingType = async (idContract: string) => {
      const requestBody: GetBillingTypeRequestBody = {
        contractId: +idContract,
        isAdmin: ownerData?.isAdministrator || false,
      };

      try {
        const { Codigo: code } = await getBillingType(
          requestBody,
          userData?.token || "",
          new AbortController().signal
        );

        setInitialValue(code);
        setSelectedValue(code);
      } catch (error) {
        console.error("Error fetching initial billing type: ", error);
      }
    };

    if (suppliesDetail && ownerData) {
      const cupWithDesiredContract = ownerData.cups.find(
        (cup) => +cup.contractCode === +suppliesDetail.codeContract
      );

      if (cupWithDesiredContract?.idContract) {
        setSupplyContractId(cupWithDesiredContract.idContract);
        fetchInitialBillingType(cupWithDesiredContract.idContract);
      }
    }
  }, [ownerData, suppliesDetail, userData?.token]);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value;
    setSelectedValue(selectedId);

    if (selectedId === "Q" || selectedId === "P") {
      setShowModal(true);
    } else {
      handleSendRequest(selectedId);
    }
  };

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const handleModalClose = () => {
    setSelectedValue(initialValue);
    setShowModal(false);
    setIsChecked(false);
  };

  const handleSendRequest = async (option: string) => {
    const requestBody: SetBillingTypeRequestBody = {
      contractId: +supplyContractId,
      communicationTypeCode: option,
    };

    try {
      await setBillingType(
        requestBody,
        userData?.token || "",
        new AbortController().signal
      ).then(() => {
        setSnackbarData({
          isOpen: true,
          isSuccessSeverity: true,
          text: "Se ha actualizado con éxito el modo de envío de las facturas",
        });
      });
      setShowModal(false);
      setIsChecked(false);
    } catch (error) {
      console.error("Error setting billing type: ", error);
    }
  };

  return (
    <Stack
      direction="column"
      rowGap={{ xs: "24px", md: "40px" }}
      component="article"
      sx={{ minHeight: !!idCups ? "16.75rem" : "auto" }}
      className="bills-header"
    >
      <SnackbarComponent
        snackbarData={snackbarData}
        setSnackbarData={setSnackbarData}
      />
      {isHeaderLoading ? (
        <LoadingComponentComponent
          isLoading={isHeaderLoading}
          position="absolute"
        />
      ) : (
        <>
          {isDesktopViewport && !!idCups && (
            <Container component="div">
              <Breadcrumbs aria-label="breadcrumb">
                <Link
                  component="button"
                  underline="hover"
                  onClick={() => navigate(process.env.REACT_APP_SUPPLIES!)}
                >
                  {t("Suministros.Suministros")}
                </Link>
                <Link
                  component="button"
                  underline="hover"
                  onClick={() =>
                    navigate(
                      `${process.env.REACT_APP_SUPPLIES}/${idCups}?origin=bills`
                    )
                  }
                >
                  {`${t("Tablas.CUPS")} ${suppliesDetail.cupsCode}`}
                </Link>
                <Typography component="p" variant="caption">
                  {t("Header.Nav.Facturas")}
                </Typography>
              </Breadcrumbs>
            </Container>
          )}
          <Container
            component="div"
            className="bills-header__title-container"
            sx={{ display: "flex" }}
          >
            <Typography variant="h1" component="h1">
              {t("Header.Nav.Facturas")}
            </Typography>
            <Box
              component="div"
              className="bills-header__supplies-bills-container"
            >
              <Stack
                component="div"
                display={"flex"}
                alignItems={"center"}
                flexDirection={"row"}
                gap={"1rem"}
              >
                {suppliesBillsHeaderData.map(({ id, label, classNameIcon }) => (
                  <Box
                    key={id}
                    component="div"
                    className="bills-header__supplies-bills"
                  >
                    <span className={`${classNameIcon} bills-header__icon`} />
                    <Typography component="p" variant="body1">
                      {formatNumber(+totalSuppliesBills[id] || 0)}{" "}
                      {t(`Header.Nav.${label}`)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Container>
          {!!idCups && (
            <Container
              component="div"
              className="bills-header__cups-information"
              sx={{ display: "flex" }}
            >
              {cupsData.map(({ id, label, value, renderContent }) => (
                <Stack key={id} component="div">
                  <Typography
                    component="span"
                    variant="body1"
                    className="information-label"
                  >
                    {t(label)}
                  </Typography>
                  {renderContent ? (
                    renderContent(value)
                  ) : (
                    <Typography component="span" variant="tab">
                      {value}
                    </Typography>
                  )}
                </Stack>
              ))}

              <Stack component="div">
                <FormControl>
                  <FormLabel htmlFor="e-billing">
                    <Typography
                      component="span"
                      variant="body1"
                      className="information-label"
                    >
                      {t(`Facturas Página.Envío de factura`)}
                    </Typography>
                  </FormLabel>
                  <Select
                    sx={{ width: "20rem" }}
                    value={selectedValue}
                    onChange={handleSelectChange}
                  >
                    {menuItemsBilling.map(({ id, label }) => (
                      <MenuItem key={id} value={id}>
                        {t(`Facturas Página.${label}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {showModal && (
                <Dialog
                  onClose={handleModalClose}
                  open={true}
                  className="eBills-dialog"
                >
                  <Stack
                    gap={"1.5rem"}
                    paddingBottom={{ xs: "1rem", md: "2.5rem" }}
                  >
                    <Stack
                      direction={"row"}
                      padding={0}
                      justifyContent="space-between"
                    >
                      <Typography variant="h2" component="p">
                        {t(`Facturas Página.Enviar facturas por correo postal`)}
                      </Typography>
                      <Button
                        onClick={handleModalClose}
                        sx={{ alignSelf: "start" }}
                      >
                        <Typography
                          fontSize="32px"
                          color={theme.palette.grey[900]}
                        >
                          <span className="t-icon-close"></span>
                        </Typography>
                      </Button>
                    </Stack>
                    <Typography variant="subtitle1" component="p">
                      {t(
                        `Facturas Página.Aceptar enviar facturas por correo postal`
                      )}
                    </Typography>
                    <FormControlLabel
                      sx={{ margin: "0", gap: "1rem" }}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label={t(
                        `Facturas Página.Checkbox acepto enviar facturas por correo postal`
                      )}
                    />
                  </Stack>
                  <Stack gap={"1rem"}>
                    <ButtonElement
                      onClick={() => handleSendRequest(selectedValue)}
                      disabled={!isChecked}
                    >
                      {t(`Facturas Página.Activar envío postal`)}
                    </ButtonElement>
                    <ButtonElement onClick={handleModalClose} link>
                      {t(`Cancelar`)}
                    </ButtonElement>
                  </Stack>
                </Dialog>
              )}
            </Container>
          )}
        </>
      )}
      <Container
        component="div"
        className="bills-header__tabs-container"
        sx={{ display: "flex" }}
      >
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={selectedTab}
            onChange={(event: React.SyntheticEvent, newValue: string) => {
              setSelectedTab(newValue);
            }}
            component={"ul"}
            sx={{
              ".MuiTabs-indicator": {
                left: 0,
              },
              paddingLeft: 0,
            }}
          >
            {tabsDataBillsHeader.map(({ id, label, value }) => (
              <Tab
                key={id}
                component="a"
                label={
                  <Typography component="span" variant="tab">
                    {t(`Facturas Página.${label}`)}
                  </Typography>
                }
                sx={{ color: theme.palette.grey[500] }}
                value={value}
                className="tab"
                disableRipple
              />
            ))}
          </Tabs>
        </Box>
      </Container>
    </Stack>
  );
};

export default BillsHeader;
